import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import type {
  Asset,
  Citation,
  Document,
  Message,
  NewMessage,
  Project,
  Question,
  QuestionUpdate,
  Space,
} from "@scenix/core";
import { formatTimecode, outstanding } from "@scenix/core";
import { ApiError, api } from "../lib/api";
import { RefusalLine, type Refusal } from "./RefusalLine";

/**
 * Where a refused write belongs on this panel (ADR 000130).
 *
 * Every write here is fired from one of three places: the ask form, the
 * message composer, or the controls inside one question's row. Answering,
 * resolving, escalating and dismissing all belong to the question they are
 * about, so they share its place.
 */
type Place = "ask" | "message" | `question:${Question["id"]}`;

interface ThreadPanelProps {
  spaceId: Space["id"];
  projectId: Project["id"];
  /**
   * Whether the caller may write to this project. Posting and asking are
   * Viewer-permitted on the API, same as leaving a note (a reviewer who
   * cannot edit is exactly who most needs to take part); resolving or
   * dismissing a question is Editor-only, the same editorial line
   * handleUpdateNote draws, so those actions are hidden rather than left to
   * fail for a viewer.
   */
  mayEdit: boolean;
}

/**
 * The project's shared question thread: what has been said, what is still
 * owed an answer, and what a person has accepted as decided.
 *
 * A message answers a question by naming it; that alone moves the question to
 * answered; a person still has to resolve it before it counts as settled
 * (ADR 000012).
 */
export function ThreadPanel({ spaceId, projectId, mayEdit }: ThreadPanelProps) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState("");
  const [noteSources, setNoteSources] = useState<Citation[]>([]);
  const [prompt, setPrompt] = useState("");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  // What each answer in progress cites, keyed the way its text is.
  const [answerSources, setAnswerSources] = useState<Record<string, Citation[]>>({});
  const [failure, setFailure] = useState<Refusal<Place> | null>(null);
  // The question whose resolution is being written, if any. One at a time:
  // accepting is a decision somebody sits with, not a queue to work through.
  const [resolving, setResolving] = useState<Question["id"] | null>(null);
  // A resolution in progress, held past a cancel so reopening the form does
  // not lose the paragraph somebody wrote. Only recording it clears one.
  const [resolutions, setResolutions] = useState<Record<string, string>>({});
  const [resolutionSources, setResolutionSources] = useState<Record<string, Citation[]>>({});

  const messagesKey = ["thread-messages", spaceId, projectId];
  const questionsKey = ["thread-questions", spaceId, projectId];

  function handleFailure(where: Place, error: unknown) {
    setFailure({ where, message: describe(error) });
  }

  const messages = useQuery({
    queryKey: messagesKey,
    queryFn: () => api.listMessages(spaceId, projectId),
  });

  const questions = useQuery({
    queryKey: questionsKey,
    queryFn: () => api.listQuestions(spaceId, projectId),
  });

  // Citations point at documents and assets by id (ADR 000012). Both queries
  // share a cache key with the panels that already fetch them elsewhere on
  // the project page, so this costs no extra request in the common case.
  const documents = useQuery({
    queryKey: ["documents", spaceId, projectId],
    queryFn: () => api.listDocuments(spaceId, projectId),
  });
  const assets = useQuery({
    queryKey: ["assets", spaceId, ""],
    queryFn: () => api.listAssets(spaceId),
  });
  // Every id a citation on this screen points at, from both places one can
  // appear: a message and the resolution a question ended in (ADR 000012).
  const cited = useMemo(
    () => [
      ...(messages.data ?? []).flatMap((message) => message.citations),
      ...(questions.data ?? []).flatMap((question) => question.resolution?.citations ?? []),
    ],
    [messages.data, questions.data],
  );

  const documentNames = useMemo(() => named(documents.data, "title"), [documents.data]);
  const assetNames = useMemo(() => named(assets.data, "name"), [assets.data]);

  // A cited id the ordinary listing does not have is either inside the
  // recovery window or purged past it, and only the first is worth telling
  // somebody about, because only the first can be put back (ADR 000055).
  // Reading the deleted listing is the only thing that tells them apart, so
  // it is asked for once something is actually missing and not before: a
  // thread whose citations all resolve pays nothing for this. The wait for
  // the ordinary listing matters too, since every id looks missing until it
  // lands (issue #293).
  const documentMissing = documents.isSuccess && cited.some(absent("documentId", documentNames));
  const assetMissing = assets.isSuccess && cited.some(absent("assetId", assetNames));

  const deletedDocuments = useQuery({
    queryKey: ["documents", spaceId, projectId, "deleted"],
    queryFn: () => api.listDocuments(spaceId, projectId, { deleted: true }),
    enabled: documentMissing,
  });
  const deletedAssets = useQuery({
    queryKey: ["assets", spaceId, "deleted"],
    queryFn: () => api.listAssets(spaceId, { deleted: true }),
    enabled: assetMissing,
  });

  const removedDocumentNames = useMemo(
    () => named(deletedDocuments.data, "title"),
    [deletedDocuments.data],
  );
  const removedAssetNames = useMemo(() => named(deletedAssets.data, "name"), [deletedAssets.data]);

  // An error settles whether we will find out, not what is there, and
  // resolveSource keeps those apart rather than reading a failed listing as
  // an empty one.
  const documentSources: SourceIndex = {
    live: { names: documentNames, settled: documents.isSuccess, failed: documents.isError },
    removed: {
      names: removedDocumentNames,
      settled: deletedDocuments.isSuccess,
      failed: deletedDocuments.isError,
    },
  };
  const assetSources: SourceIndex = {
    live: { names: assetNames, settled: assets.isSuccess, failed: assets.isError },
    removed: {
      names: removedAssetNames,
      settled: deletedAssets.isSuccess,
      failed: deletedAssets.isError,
    },
  };

  // A resolution names who accepted it by id, and an id is not a name a reader
  // can go back to (ADR 000077). The space's membership is what turns one into
  // the other, and it holds everybody who ever arrived rather than only who is
  // here now (ADR 000059), so somebody who has since left still resolves.
  // Asked for only once a resolution on this screen actually names somebody,
  // the way the deleted listings above are: a thread that has settled nothing
  // pays no request for this. Listing members is viewer-permitted, so this is
  // no narrower than the panel itself.
  const namesAnAcceptor = (questions.data ?? []).some((question) =>
    Boolean(question.resolution?.resolvedBy),
  );
  // Handing a question on needs the list before anybody has been named, so an
  // editor with something outstanding pays for it where a viewer reading a
  // settled thread still does not (ADR 000084).
  const namesAnAssignee = (questions.data ?? []).some((question) => Boolean(question.assignedTo));
  const mayAssign = mayEdit && (questions.data ?? []).some(awaitsAnAnswer);
  const members = useQuery({
    queryKey: ["members", spaceId],
    queryFn: () => api.listMembers(spaceId),
    enabled: namesAnAcceptor || namesAnAssignee || mayAssign,
  });
  const memberNames = useMemo(
    () => new Map((members.data ?? []).map((member) => [member.user.id, member.user.displayName])),
    [members.data],
  );
  // Only somebody still here can be given one: the API refuses an inactive
  // member, so offering one would be offering a refusal (ADR 000059).
  const assignable = useMemo(
    () => (members.data ?? []).filter((member) => member.status === "active"),
    [members.data],
  );

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: messagesKey });
    void queryClient.invalidateQueries({ queryKey: questionsKey });
  };

  const postMessage = useMutation({
    mutationFn: (draft: Parameters<typeof api.postMessage>[2]) =>
      api.postMessage(spaceId, projectId, draft),
    onSuccess: (_, draft) => {
      invalidate();
      setFailure(null);
      // Only a write that landed clears what it was written from. A draft held
      // past a refusal is somebody's own words and the sources they picked one
      // at a time, and the answer to a 429 or a bad minute on the API is to
      // send the same thing again (ADR 000048).
      const answered = draft.answersQuestionId;
      if (answered) {
        setDrafts((prev) => without(prev, answered));
        setAnswerSources((prev) => without(prev, answered));
      } else {
        setNote("");
        setNoteSources([]);
      }
    },
    onError: (error, draft) =>
      handleFailure(
        draft.answersQuestionId ? `question:${draft.answersQuestionId}` : "message",
        error,
      ),
  });

  const askQuestion = useMutation({
    mutationFn: (p: string) => api.askQuestion(spaceId, projectId, { prompt: p }),
    onSuccess: () => {
      invalidate();
      setPrompt("");
      setFailure(null);
    },
    onError: (error) => handleFailure("ask", error),
  });

  const updateQuestion = useMutation({
    mutationFn: (change: Parameters<typeof api.updateQuestion>[2]) =>
      api.updateQuestion(spaceId, projectId, change),
    onSuccess: (_, change) => {
      invalidate();
      setFailure(null);
      // The record exists now, so there is nothing left worth keeping the
      // draft for; the question itself will render what was written.
      if (change.status === "resolved") {
        setResolving(null);
        setResolutions((prev) => without(prev, change.id));
        setResolutionSources((prev) => without(prev, change.id));
      }
    },
    onError: (error, change) => handleFailure(`question:${change.id}`, error),
  });

  /**
   * Open the resolution form on a question, started from the proposal when
   * there is one, since resolving is usually accepting what was already said
   * rather than writing it fresh. Its citations come across as chips that can
   * be taken off, so what the record will rest on is on screen and editable
   * rather than inherited out of sight (ADR 000077).
   *
   * Seeded once: a draft that survived a cancel is somebody's own words, and
   * overwriting it with the proposal again would lose an edit the way the
   * dialog this replaced did.
   */
  function openResolution(question: Question, proposal: Message | undefined) {
    setResolving(question.id);
    setResolutions((prev) =>
      question.id in prev ? prev : { ...prev, [question.id]: proposal?.body ?? "" },
    );
    setResolutionSources((prev) =>
      question.id in prev ? prev : { ...prev, [question.id]: proposal?.citations ?? [] },
    );
  }

  if (messages.isPending || questions.isPending) return <p role="status">Loading the thread…</p>;
  if (messages.error) return <p className="error">{describe(messages.error)}</p>;
  if (questions.error) return <p className="error">{describe(questions.error)}</p>;

  const allMessages = messages.data;
  const allQuestions = questions.data;
  const ordered = [
    ...outstanding(allQuestions),
    ...allQuestions.filter((q) => q.status === "resolved" || q.status === "dismissed"),
  ];
  const busy = postMessage.isPending || askQuestion.isPending || updateQuestion.isPending;

  return (
    <section className="thread">
      <h2 className="section-head">Thread</h2>

      <h3 className="section-head">Questions</h3>
      {ordered.length === 0 ? (
        <p className="muted">Nothing has been asked yet.</p>
      ) : (
        <ul className="questions">
          {ordered.map((question) => {
            // The message that moved this question to answered is the proposal
            // itself (ADR 000012). It is what the row shows, what the
            // resolution form starts from, and where its citations come from,
            // so it is found once rather than at each of the three.
            const proposal = proposalFor(allMessages, question.id);
            const resolvedBy = question.resolution?.resolvedBy;
            const acceptor = resolvedBy ? memberNames.get(resolvedBy) : undefined;
            const owner = question.assignedTo ? memberNames.get(question.assignedTo) : undefined;
            const resolution = resolutions[question.id] ?? "";
            const resolutionCitations = resolutionSources[question.id] ?? [];
            return (
              <li key={question.id} className={`question is-${question.status}`}>
                <p>{question.prompt}</p>
                <span className={`question-status is-${question.status}`}>
                  {question.status.replace("_", " ")}
                </span>
                {/* Who asked is part of what the question means: a length a
                    script noticed reads differently from a colleague's doubt
                    (ADR 000058). */}
                {question.authorKind === "assistant" && (
                  <>
                    {/* A space of its own: JSX emits none around an element on
                        its own line, so without this the row reads as one word
                        to anything working from the text (issue #310). */}{" "}
                    <span className="badge" title="Raised by an agent, not a person">
                      agent
                    </span>
                  </>
                )}

                {/* Who owes the answer, which is the difference between a
                    question in front of somebody and one in front of the list
                    (ADR 000084). A name that does not resolve is somebody the
                    membership listing no longer carries, and saying the id
                    would name nobody, so it says that instead. */}
                {question.assignedTo && (
                  <p className="muted">For {owner ?? "somebody no longer in this space"}</p>
                )}

                {question.resolution && (
                  <>
                    <p className="muted">
                      Resolved: {question.resolution.answer}
                      {/* A model's sentence accepted unchanged and a producer's
                          own read identically three weeks later, and they carry
                          different weight when somebody is deciding whether to
                          reopen the question (ADR 000077). An answer somebody
                          typed or edited before accepting is the ordinary case
                          and is not decorated. */}
                      {question.resolution.machineGenerated && (
                        <span className="badge" title="Accepted as a model wrote it, unedited">
                          from a model
                        </span>
                      )}
                    </p>
                    {/* Who accepted it is who to go back to and ask why. Only
                        said when there is a name to say: an id is not one, and
                        a resolution written with no acceptor at all names
                        nobody, so calling that an agent would be a claim the
                        record does not make. */}
                    {acceptor && <p className="muted">Accepted by {acceptor}</p>}
                    <CitationList
                      citations={question.resolution.citations}
                      spaceId={spaceId}
                      documents={documentSources}
                      assets={assetSources}
                    />
                  </>
                )}

                {/* Shown here so accepting or dismissing a proposal does not
                    need a trip down to the messages list to find what is
                    actually being decided. Its citations come with it, because
                    checking the sources is what accepting means (ADR 000044)
                    and the form below is written while reading them. */}
                {question.status === "answered" && proposal && (
                  <>
                    <p className="muted">
                      Proposed by {proposal.authorKind}: {proposal.body}
                    </p>
                    <CitationList
                      citations={proposal.citations}
                      spaceId={spaceId}
                      documents={documentSources}
                      assets={assetSources}
                    />
                  </>
                )}

                {awaitsAnAnswer(question) && (
                  <form
                    className="answer-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const body = (drafts[question.id] ?? "").trim();
                      if (!body) return;
                      postMessage.mutate(
                        messageDraft(body, answerSources[question.id] ?? [], question.id),
                      );
                    }}
                  >
                    <textarea
                      rows={2}
                      placeholder="Answer this question"
                      value={drafts[question.id] ?? ""}
                      onChange={(event) =>
                        setDrafts((prev) => ({ ...prev, [question.id]: event.target.value }))
                      }
                    />
                    <SourcePicker
                      id={`cite-${question.id}`}
                      label="Cite a source for this answer"
                      documents={documents.data ?? []}
                      assets={assets.data ?? []}
                      citations={answerSources[question.id] ?? []}
                      onChange={(citations) =>
                        setAnswerSources((prev) => ({ ...prev, [question.id]: citations }))
                      }
                    />
                    <button type="submit" disabled={busy || !(drafts[question.id] ?? "").trim()}>
                      Answer
                    </button>
                  </form>
                )}

                {mayEdit && question.status === "answered" && resolving !== question.id && (
                  <div className="question-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => openResolution(question, proposal)}
                    >
                      Resolve
                    </button>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() =>
                        updateQuestion.mutate({ id: question.id, status: "dismissed" })
                      }
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                {/* The record ADR 000012 is about, written where it can be read
                    against the proposal and the sources above it rather than
                    under a dialog drawn over them. */}
                {mayEdit && question.status === "answered" && resolving === question.id && (
                  <form
                    className="answer-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      const answer = resolution.trim();
                      if (!answer) return;
                      updateQuestion.mutate(
                        resolutionUpdate(question.id, answer, resolutionCitations),
                      );
                    }}
                  >
                    <label htmlFor={`resolve-${question.id}`}>Resolve this question</label>
                    <textarea
                      id={`resolve-${question.id}`}
                      rows={4}
                      value={resolution}
                      onChange={(event) =>
                        setResolutions((prev) => ({ ...prev, [question.id]: event.target.value }))
                      }
                      autoFocus
                    />
                    <SourcePicker
                      id={`cite-resolution-${question.id}`}
                      label="Cite a source for this resolution"
                      documents={documents.data ?? []}
                      assets={assets.data ?? []}
                      citations={resolutionCitations}
                      onChange={(citations) =>
                        setResolutionSources((prev) => ({ ...prev, [question.id]: citations }))
                      }
                    />
                    {/* Taking every chip off does not record a resolution that
                        rests on nothing: one that names no source keeps the
                        proposal's, so say which of the two is about to
                        happen (ADR 000077). Saying it is the most this form
                        can do while the wire reads an empty list and an absent
                        one the same way (issue #306). */}
                    {resolutionCitations.length === 0 && proposal?.citations.length ? (
                      <p className="muted">
                        With no source chosen, the record keeps the ones the proposal cited.
                      </p>
                    ) : null}
                    <div className="question-actions">
                      <button type="submit" disabled={busy || !resolution.trim()}>
                        Record
                      </button>
                      <button
                        type="button"
                        className="ghost"
                        disabled={busy}
                        onClick={() => setResolving(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {mayEdit && awaitsAnAnswer(question) && (
                  <div className="question-actions">
                    {/* Choosing somebody is the escalation, not a step before
                        one: it moves the question to needs_human in the same
                        write, so a question can never read as open while
                        sitting on a named person (ADR 000084). Taking the name
                        off leaves the status where it is, because a question
                        somebody was asked and then unassigned still needs a
                        human. */}
                    <label htmlFor={`assign-${question.id}`}>For</label>
                    <select
                      id={`assign-${question.id}`}
                      value={question.assignedTo ?? ""}
                      disabled={busy || assignable.length === 0}
                      onChange={(event) =>
                        updateQuestion.mutate({
                          id: question.id,
                          status: event.target.value ? "needs_human" : question.status,
                          assignedTo: event.target.value,
                        })
                      }
                    >
                      <option value="">Nobody in particular</option>
                      {/* Somebody assigned before they left is still who this
                          is on, and dropping their option would make the
                          picker read as nobody. Kept and unselectable: it can
                          be moved off them, not back onto them. */}
                      {question.assignedTo &&
                        !assignable.some((m) => m.user.id === question.assignedTo) && (
                          <option value={question.assignedTo} disabled>
                            {owner ?? "somebody no longer in this space"}
                          </option>
                        )}
                      {assignable.map((member) => (
                        <option key={member.user.id} value={member.user.id}>
                          {member.user.displayName}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="ghost"
                      disabled={busy}
                      onClick={() =>
                        updateQuestion.mutate({ id: question.id, status: "dismissed" })
                      }
                    >
                      Dismiss
                    </button>
                  </div>
                )}

                <RefusalLine refusal={failure} at={`question:${question.id}`} />
              </li>
            );
          })}
        </ul>
      )}

      <form
        className="range-comment"
        onSubmit={(event) => {
          event.preventDefault();
          const value = prompt.trim();
          if (!value) return;
          askQuestion.mutate(value);
        }}
      >
        <label htmlFor="ask-question">Ask a question</label>
        <textarea
          id="ask-question"
          rows={2}
          value={prompt}
          placeholder="What is the runtime?"
          onChange={(event) => setPrompt(event.target.value)}
        />
        <button type="submit" disabled={busy || prompt.trim() === ""}>
          Ask
        </button>
        <RefusalLine refusal={failure} at="ask" />
      </form>

      <h3 className="section-head">Messages</h3>
      {allMessages.length === 0 ? (
        <p className="muted">No messages yet.</p>
      ) : (
        <ul className="messages">
          {allMessages.map((message) => (
            <li key={message.id} className="message">
              <p>{message.body}</p>
              <span className="muted">{message.authorKind}</span>
              <CitationList
                citations={message.citations}
                spaceId={spaceId}
                documents={documentSources}
                assets={assetSources}
              />
            </li>
          ))}
        </ul>
      )}

      <form
        className="range-comment"
        onSubmit={(event) => {
          event.preventDefault();
          const body = note.trim();
          if (!body) return;
          postMessage.mutate(messageDraft(body, noteSources));
        }}
      >
        <label htmlFor="thread-note">Say something</label>
        <textarea
          id="thread-note"
          rows={2}
          value={note}
          placeholder="Not a question, just a note for the room."
          onChange={(event) => setNote(event.target.value)}
        />
        <SourcePicker
          id="cite-thread-note"
          label="Cite a source"
          documents={documents.data ?? []}
          assets={assets.data ?? []}
          citations={noteSources}
          onChange={setNoteSources}
        />
        <button type="submit" disabled={busy || note.trim() === ""}>
          Post
        </button>
        <RefusalLine refusal={failure} at="message" />
      </form>
    </section>
  );
}

/**
 * The message a form is about to post. Citations go on it only when there are
 * some, because an absent field and an empty list mean the same thing to the
 * API and the shorter one says what happened (ADR 000077).
 */
function messageDraft(
  body: string,
  citations: Citation[],
  answersQuestionId?: Question["id"],
): NewMessage {
  const draft: NewMessage = { body };
  if (citations.length > 0) draft.citations = citations;
  if (answersQuestionId) draft.answersQuestionId = answersQuestionId;
  return draft;
}

/**
 * Choosing what a message rests on, from what the project already has open.
 *
 * A whole document or a whole piece of content, never part of one: a span of
 * characters and a range of footage each need a selection surface that does not
 * exist yet, and a field that can only be filled by typing a `doc_…` is not a
 * write path anybody has (ADR 000077). A document is cited at the revision this
 * listing is showing, so the citation says which text was meant.
 */
function SourcePicker({
  id,
  label,
  documents,
  assets,
  citations,
  onChange,
}: {
  id: string;
  /**
   * What this picker is for, in words. Two of them are on screen at once
   * whenever a question is open, and "Cite a source" twice tells somebody
   * reading the page with their ears nothing about which is which.
   */
  label: string;
  documents: Document[];
  assets: Asset[];
  citations: Citation[];
  onChange: (citations: Citation[]) => void;
}) {
  // Already cited is not offered again. Citing the same document twice says
  // nothing the first one did not, and the shrinking list is what tells
  // somebody what they have already named.
  const taken = new Set(citations.map(sourceKey));
  const choices = [
    ...documents.map((document) => ({
      key: `document:${document.id}`,
      label: document.title,
      group: "Documents",
      citation: {
        kind: "document" as const,
        documentId: document.id,
        revision: document.revision,
      },
    })),
    ...assets.map((asset) => ({
      key: `asset:${asset.id}`,
      label: asset.name,
      group: "Content",
      citation: { kind: "asset" as const, assetId: asset.id },
    })),
  ].filter((choice) => !taken.has(choice.key));

  return (
    <div className="citation-picker">
      {citations.length > 0 && (
        <ul className="citation-choices">
          {citations.map((citation, index) => (
            <li key={sourceKey(citation)} className="citation-choice">
              <span>{chosenName(citation, documents, assets)}</span>
              <button
                type="button"
                className="ghost"
                onClick={() => onChange(citations.filter((_, at) => at !== index))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        // Held at the placeholder, because choosing is what adds a citation
        // rather than what this control is showing: the chosen ones are the
        // list above.
        value=""
        disabled={choices.length === 0}
        onChange={(event) => {
          const chosen = choices.find((choice) => choice.key === event.target.value);
          if (chosen) onChange([...citations, chosen.citation]);
        }}
      >
        {/* The control sits at this option whatever has been chosen, so it
            says what choosing does rather than what is chosen. */}
        <option value="">{choices.length === 0 ? "Nothing left to cite" : "Choose one…"}</option>
        {["Documents", "Content"].map((group) => {
          const inGroup = choices.filter((choice) => choice.group === group);
          if (inGroup.length === 0) return null;
          return (
            <optgroup key={group} label={group}>
              {inGroup.map((choice) => (
                <option key={choice.key} value={choice.key}>
                  {choice.label}
                </option>
              ))}
            </optgroup>
          );
        })}
      </select>
    </div>
  );
}

/** What a citation points at, as one string, for comparing and for keys. */
function sourceKey(citation: Citation): string {
  return citation.kind === "document"
    ? `document:${citation.documentId}`
    : `asset:${citation.assetId}`;
}

/** What a citation being written names, from the listings the picker offered. */
function chosenName(citation: Citation, documents: Document[], assets: Asset[]): string {
  if (citation.kind === "document") {
    return documents.find((document) => document.id === citation.documentId)?.title ?? "a document";
  }
  return assets.find((asset) => asset.id === citation.assetId)?.name ?? "an asset";
}

/**
 * The resolution a form is about to record. Citations go on it only when there
 * are some, for the same reason a message's do, and with one more: a resolution
 * that names none is read as accepting what the proposal rested on, so an empty
 * list would say what an absent one already says (ADR 000077).
 */
function resolutionUpdate(
  id: Question["id"],
  answer: string,
  citations: Citation[],
): QuestionUpdate & { id: Question["id"] } {
  const change: QuestionUpdate & { id: Question["id"] } = { id, status: "resolved", answer };
  if (citations.length > 0) change.citations = citations;
  return change;
}

/** One key dropped from a record of drafts, leaving the rest alone. */
function without<T>(drafts: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _dropped, ...rest } = drafts;
  return rest;
}

/**
 * The message that moved a question to answered, if any: the proposal a
 * person is being asked to accept or dismiss. Latest first, in case a
 * question was reopened and answered more than once.
 */
function proposalFor(messages: Message[], questionId: Question["id"]): Message | undefined {
  return messages.filter((m) => m.answersQuestionId === questionId).at(-1);
}

/**
 * Whether a question is still owed an answer: nobody has proposed one, or
 * somebody has said the material does not settle it. The same set can be
 * answered, dismissed, and handed to a person, so all three ask this.
 */
function awaitsAnAnswer(question: Question): boolean {
  return question.status === "open" || question.status === "needs_human";
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.userMessage;
  return error instanceof Error ? error.message : "Something went wrong.";
}

/** Names by id from one listing, and how far that listing has got. */
interface Listing {
  names: Map<string, string>;
  settled: boolean;
  failed: boolean;
}

/** The two listings a cited id is looked up in: this side of the window and the other. */
interface SourceIndex {
  live: Listing;
  removed: Listing;
}

/**
 * What a citation's target turned out to be. These render differently on
 * purpose, because a reader who cannot tell them apart cannot tell a record
 * that still supports itself from one that has quietly stopped (ADR 000012).
 */
type Source =
  | { state: "unnamed" }
  | { state: "pending" }
  | { state: "present"; name: string }
  | { state: "deleted"; name: string }
  | { state: "gone" }
  | { state: "unknown" };

function resolveSource(id: string | undefined, { live, removed }: SourceIndex): Source {
  // A citation that names no id is not a source that went missing; it never
  // pointed anywhere, and saying "deleted" about it would be a lie.
  if (!id) return { state: "unnamed" };

  // An unanswered listing holds no ids, so reading one before it settles says
  // every source is missing. The deleted listing in particular can be settled
  // first, out of the cache the Deleted screen filled, which is what makes
  // this a guard rather than a formality (issue #293).
  if (live.failed) return { state: "unknown" };
  if (!live.settled) return { state: "pending" };

  const name = live.names.get(id);
  if (name !== undefined) return { state: "present", name };

  // Absent from the ordinary listing. Whether it is restorable is the
  // recovery window's answer to give (ADR 000055), so wait for it, and say
  // plainly when it cannot be reached rather than guessing either way.
  if (removed.failed) return { state: "unknown" };
  if (!removed.settled) return { state: "pending" };

  const wasNamed = removed.names.get(id);
  if (wasNamed !== undefined) return { state: "deleted", name: wasNamed };
  return { state: "gone" };
}

/** How a source is named once it has been looked up. */
function sourceName(source: Source, noun: string): string {
  switch (source.state) {
    case "pending":
      return "…";
    case "present":
    case "deleted":
      return source.name;
    default:
      return `a ${noun}`;
  }
}

/**
 * The part of the label that is about the source rather than the claim. Only
 * the states a reader has to act on differently carry one: a restore is worth
 * offering for a deleted source and impossible for a purged one.
 */
function SourceNote({ source }: { source: Source }) {
  switch (source.state) {
    case "deleted":
      return <span className="citation-state">deleted, and can be restored</span>;
    case "gone":
      return <span className="citation-state">deleted, and past restoring</span>;
    case "unknown":
      return <span className="citation-state">could not be looked up</span>;
    default:
      return null;
  }
}

/**
 * What a claim was based on, so it can be checked rather than trusted
 * (ADR 000012). A citation carries only ids and offsets; this is where they
 * become something a person can read and, for an asset, follow.
 */
function CitationList({
  citations,
  spaceId,
  documents,
  assets,
}: {
  citations: Citation[];
  spaceId: Space["id"];
  documents: SourceIndex;
  assets: SourceIndex;
}) {
  if (citations.length === 0) return null;
  return (
    <ul className="citations">
      {citations.map((citation, index) => (
        <li key={index} className="citation">
          {citation.kind === "document"
            ? documentCitationLabel(citation, resolveSource(citation.documentId, documents))
            : assetCitationLabel(citation, spaceId, resolveSource(citation.assetId, assets))}
        </li>
      ))}
    </ul>
  );
}

function documentCitationLabel(citation: Citation, source: Source) {
  const hasSpan = citation.startOffset != null && citation.endOffset != null;
  const span = hasSpan ? ` (chars ${citation.startOffset}–${citation.endOffset})` : "";
  const revision = citation.revision != null ? `, as of revision ${citation.revision}` : "";
  return (
    <span className="citation-label">
      {sourceName(source, "document")}
      {span}
      {revision}
      <SourceNote source={source} />
    </span>
  );
}

function assetCitationLabel(citation: Citation, spaceId: Space["id"], source: Source) {
  const range = citation.range
    ? ` (${formatTimecode(citation.range.startSec)}–${formatTimecode(citation.range.endSec)})`
    : "";
  const body = (
    <>
      {sourceName(source, "asset")}
      {range}
      <SourceNote source={source} />
    </>
  );
  // Only a live asset has a page to open. Deleted and purged ones both answer
  // the content route as gone, so a link there is a dead end dressed as a way
  // to check the claim.
  if (source.state !== "present") return <span className="citation-label">{body}</span>;
  return (
    <Link to={`/spaces/${spaceId}/content/${citation.assetId}`} className="citation-label">
      {body}
    </Link>
  );
}

/** Names by id, for whichever field the listing calls its name. */
function named<K extends string>(
  rows: ({ id: string } & Record<K, string>)[] | undefined,
  field: K,
): Map<string, string> {
  return new Map((rows ?? []).map((row) => [row.id, row[field]]));
}

/** A citation pointing at an id the given listing does not carry. */
function absent(field: "documentId" | "assetId", names: Map<string, string>) {
  return (citation: Citation) => {
    const id = citation[field];
    return id != null && !names.has(id);
  };
}
